import React, { useEffect, useMemo, useState } from 'react';
import type { DateType, DateValueType } from '../../types';
import TimePicker from './TimePicker';
import {
  useSetPeriodDefaultValue,
  useSetTimeDefaultValue,
} from '../../hooks/init';
import { commonBackInfo } from '../../utils';

import './index.scss';
import { defaultRangePickerValue } from '../../constants/date';
import RangeDayMonthSelector from './RangeDayMonthSelector';
import { formatTimeToServer } from '@op/utils/time';

/**
 * @param isCompetitor 是否为对比页，对比数据
 * @returns
 */
export default function DateSelect({
  defaultValue,
  filterValue,
  onChange,
  changeOnCloseWindow,
  defaultKey,
  filterOptions,
  dateTypeList = ['date', 'hour', 'week', 'month'],
  lang,
  useLaunchTime = false,
  rangePickerDefaultValue = defaultRangePickerValue,
  showShortcut = true,
  noLimitation,
  setConfirmDisable,
  needChangeSteamDateType = false,
  gameInfo,
  oriDefaultFilter,
  backOriDefaultValue,
  hasDiffClassName = true,
  ...props
}: DateType) {
  const { value, setValue, isFirst, releaseType, setReleaseType } =
    useSetPeriodDefaultValue({
      defaultValue,
      filterValue,
      initValue: ['1', '30'],
      initType: 'days',
    });

  const defaultValueMemo = useMemo(() => {
    if (
      needChangeSteamDateType &&
      filterOptions?.game_steam_date_type === 'hourly'
    ) {
      return { ...defaultValue, date_type: 'hourly' };
    }
    return defaultValue;
  }, []);

  const { dateType, setDateType, timeValue, setTimeValue, diffClassName } =
    useSetTimeDefaultValue({
      defaultValue: defaultValueMemo,
      filterValue,
      oriDefaultFilter,
      backOriDefaultValue,
    });

  // 激活状态控制
  const [disabled, setDisable] = useState('time');
  const changeDisable = (value: string) => {
    if (useLaunchTime && value !== disabled) {
      setDisable(value);
    }
  };
  const dataChange = () => {
    let result: DateValueType = {};
    let deleteKeys: Array<string> | undefined = undefined;
    const dateValue = formatTimeToServer(timeValue, dateType as any);

    if (!useLaunchTime) {
      result = {
        start_time: dateValue[0],
        end_time: dateValue[1],
        date_type: dateType,
      };
      deleteKeys = ['startPeriod', 'endPeriod', 'releaseType'];
    } else {
      result = {
        start_time: dateValue[0],
        end_time: dateValue[1],
        date_type: dateType,
        startPeriod: Number(value[0]),
        endPeriod: Number(value[1]),
        releaseType,
      };
    }
    onChange?.(
      'date',
      commonBackInfo(result, { [defaultKey]: result }),
      deleteKeys,
    );
    // 多返回一个isRelease可以做到切换的时候能通过对象对比
    changeOnCloseWindow?.({ ...result, isRelease: useLaunchTime }, deleteKeys);
  };

  // 这个写法后面可能改为编辑后立即调用onChange事件
  useEffect(() => {
    dataChange();
  }, [value, timeValue, dateType, useLaunchTime, releaseType, noLimitation]);

  const dateTypeListMemo = useMemo(() => {
    if (dateTypeList.includes('hour') || !needChangeSteamDateType) {
      return dateTypeList;
    }
    const newDateType = [...dateTypeList];

    if (filterOptions.game_steam_date_type === 'hourly') {
      newDateType.push('hour');
    }
    return newDateType;
  }, []);

  return (
    <div className={hasDiffClassName ? diffClassName : ""}>
      {useLaunchTime ? (
        <RangeDayMonthSelector
          value={value}
          defaultValue={value}
          defaultType={releaseType}
          type={releaseType}
          onConfirm={({ type, value }: any) => {
            setValue(value);
            setReleaseType(type);
          }}
          onCancel={() => {}}
        />
      ) : (
        <TimePicker
          {...props}
          rangePickerDefaultValue={rangePickerDefaultValue}
          showShortcut={showShortcut}
          onFocus={() => changeDisable('time')}
          dateTypeInfo={[dateType, setDateType]}
          dateTypeList={dateTypeListMemo}
          lang={lang}
          noLimitation={noLimitation}
          startOfTime={filterOptions.all_of_time_start_time || '2001-01-01'}
          gameInfo={gameInfo}
          timeValueInfo={[timeValue, setTimeValue]}
          setConfirmDisable={setConfirmDisable}
        />
      )}
    </div>
  );
}
